import Observable from "zen-observable"

import {
    Assessed,
    Assessment,
    AssessmentDetail,
    AssessmentTask,
    CreateAssessmentTaskInput,
    AssessmentTaskPerformanceStep,
    AssessmentTaskSubTask,
    CreateAssessmentTaskSubTaskInput,
    Branch,
    PerformanceStep,
    QuarterlyAssessmentDetail,
    Task,
    Unit,
    User,
    CreateAssessmentTaskPerformanceStepInput,
    AssessmentTaskSubTaskPerformanceStep,
    CreateAssessmentTaskSubTaskPerformanceStepInput,
    UpdateAssessmentTaskSubTaskPerformanceStepInput,
    CreateAssessmentDetailInput,
    CreateQuarterlyAssessmentDetailInput,
    ListAssessmentResult,
} from "../API"

import DGUtility from "./DGUtility.m"

import {
    GraphQLResult,
} from "@aws-amplify/api-graphql"

import ZenObservable from "zen-observable"

export type PerformanceStepFlat = {
    ID?: number,
    PSID: number,
    PerformanceStepName?: string,
    PerformanceStepSequence?: number,
    TaskNumber: string,
    TaskName: string,
    TaskID: number,
}

export type SubtaskFlat = {
    SubTaskID: number,
    SubTaskNumber: string,
    SubTaskName: string,
    ID?: string,
    TaskNumber: string,
    TaskName: string,
    TaskID: number,
    PerformanceSteps: PerformanceStepFlat[],
}

export type EMETLNestedPerformanceStep = PerformanceStepFlat & {
    Parent?: EMETLNestedSubtask | EMETLNestedTask,
}

export type EMETLNestedSubtask = SubtaskFlat & {
    Parent?: EMETLNestedTask,
    PerformanceSteps?: PerformanceStepFlat[],
}

export type EMETLNestedTask = {
    ID: number,
    Name: string,
    Number: string,
    Level: number,
    Subtasks?: EMETLNestedSubtask[],
    Parent?: EMETLNestedAssessment,
    PerformanceSteps?: EMETLNestedPerformanceStep[],
}

export type EMETLDetailedAssessment = Assessment & {
    AssessmentDetailID?: number
	Conditions?: string
	Results?: string
	Reference?: string
	IsTemplate?: number
	Location?: string
	Scenario?: string
	QuarterlyAssessmentDetailID?: number
	StartDate?: string
	EndDate?: string
}

export type EMETLAssessmentTaskSubTaskPerformanceStep = {
    ID?: number,
    ATSTID: number,
    PSID: number,
    Rating?: number,
    SpecificNeeds?: string,
}

export type EMETLBatchUpdateAssessmentTask = {
    ID: null,
    TaskID: number,
    SpecificNeeds: string,
    Rating: number,
}

export type EMETLBatchUpdateAssessed = {
    ID: null,
    UserID: string,
}

export type EMETLAssessmentTaskWithDetails = Omit<Task, "__typename"> & Omit<AssessmentTask, "__typename"> & {
    __typename?: "Task" | "AssessmentTask",
}

export type EMETLAssessmentTaskPerformanceStepWithDetails = Omit<AssessmentTaskPerformanceStep, "__typename"> & PerformanceStepFlat & {
    __typename?: "AssessmentTaskPerformanceStep",
}

export type EMETLNestedAssessment = Assessment & {
    Tasks?: {
        [taskNumber: string]: EMETLNestedTask,
    },
}

export type EMETLStaticTaskStructure = {
    [taskNumber: string]: (EMETLNestedSubtask | PerformanceStepFlat)[],
}

export type EMETLTaskStructureAPIResponseEntry = {
    ID: number,
    Level: number,
    Name: string,
    Number: string,
    PSID?: number,
    StepName?: string,
    StepSequence?: number,
    SubTaskID?: number,
    SubTaskLevel?: number, 
    SubTaskName?: string, 
    SubTaskNumber?: string,
}

export type EMETLTaskStructureAPIResponse = [EMETLTaskStructureAPIResponseEntry[], {
    affectedRows: number,
    changedRows: number,
    fieldCount?: number,
    insertId?: number,
    message: string, 
    protocol41?: boolean,
    serverStatus?: number,
    warningCount?: number,
}]

export enum EMETLTaskType {
    INDIVIDUAL = "Individual",
    COLLECTIVE = "Collective",
    LEADER = "Leader",
    SITE_SPECIFIC = "Site Specific",
}

export enum EMETLAssessmentTaskRating {
    TRAINED = 0,
    NEEDS_PRACTICE = 1,
    UNTRAINED = 2,
    NOT_APPLICABLE = 3,
}

export enum EMETLAssessmentTrainingType {
    TRAIN_OFF_POST = 0,
    TRAIN_ON_POST = 1,
    TESTING = 2,
    OBSERVATION = 3,
}

export enum EMETLPerformanceStepRating {
    GO = 0,
    NO_GO = 1,
    NOT_APPLICABLE = 2,
}
export class EMETLAssessmentTools extends DGUtility {
    /*** Private Members ***/
    #assessments: Assessment[]
    #subscriptions: ZenObservable.Subscription[]
    #handleError: (message: string) => false
    #staticTasksAndPS: EMETLStaticTaskStructure
    #PS_RATINGS = [
        "Go", 
        "No-Go", 
        "N/A",
    ]

    #TRAINING_TYPES = [
        "Train Off-Post", 
        "Train On-Post", 
        "Testing", 
        "Observation",
    ]

    #TASK_RATINGS = [
        "T", 
        "P", 
        "U", 
        "N/A",
    ]

    #units: Unit[]
    /*** Protected Members ***/
     
    /*** Private Methods ***/
 
    /*** Protected Methods ***/
     
    /**
      * Initialize and perform setup for the class
      */
    protected _init = () => {
        // Initialize or set all members based on constructor argument values 
        this.#subscriptions = []
        this.#assessments = []
        this.#handleError = (m: string) => {
            console.error(m)
             
            return false
        }
    }

    /*** Public Methods ***/
    
    /**
     * Class constructor. Calls _init() on the superclass. Add parameters as needed
     */
    public constructor() {
        super()
        this._init()
    }

    public formatCreatedDate = () => {
        const d = new Date()

        return d.toISOString().replace("T", " ").replace("Z", " ")
    }

    public createAssessment = async (
        assessment: EMETLDetailedAssessment, 
        username: string,
        assessmentTasks: string = null,
        assessed: string = null,
    ) => {
        const createdDate = this.formatCreatedDate()
       
        const resp = await this.db.executeGQL("createAssessment", {
            createAssessmentInput: {
                Title: assessment.Title || "",
                BranchID: assessment.BranchID,
                Assessor: username,
                TrainingType: assessment.TrainingType,
                Created: createdDate,
                CreateAssessmentTasks: assessmentTasks,
                CreateAssessed: assessed,
            }
        }) as Assessment
        if (!resp)
            return this.#handleError("Error creating assessment.")

        return resp
    }

    public createAssessmentDetail = async (details: CreateAssessmentDetailInput) => {
        if (details["__typename"])
            delete details["__typename"]

        const resp = await this.db.executeGQL("createAssessmentDetail", {
            createAssessmentDetailInput: details
        }) as AssessmentDetail | false
        if (!resp)
            return this.#handleError("Error creating assessment detail.")

        return resp
    }

    public createQuarterlyAssessmentDetail = async (details: CreateQuarterlyAssessmentDetailInput) => {
        if (details["__typename"])
            delete details["__typename"]
        
        const resp = await this.db.executeGQL("createQuarterlyAssessmentDetail", {
            createQuarterlyAssessmentDetailInput: details
        }) as QuarterlyAssessmentDetail | false
        if (!resp)
            return this.#handleError("Error creating quarterly assessment detail.")

        return resp
    }

    public createAssessmentTaskSubTask = async (assessmentTaskSubTask: CreateAssessmentTaskSubTaskInput) => {
        const resp = await this.db.executeGQL("createAssessmentTaskSubTask", {
            createAssessmentTaskSubTaskInput: assessmentTaskSubTask
        }) as AssessmentTaskSubTask | false
        
        if (!resp)
            return this.#handleError("Error creating assessment task subtask.")

        return resp
    }

    public createAssessmentTaskSubTaskPerformanceStep = async (
        assessmentTaskSubTaskPerformanceStep: CreateAssessmentTaskSubTaskPerformanceStepInput,
    ) => {
        const resp = await this.db.executeGQL("createAssessmentTaskSubTaskPerformanceStep", {
            createAssessmentTaskSubTaskPerformanceStepInput: assessmentTaskSubTaskPerformanceStep
        }) as AssessmentTaskSubTaskPerformanceStep | false
        
        if (!resp)
            return this.#handleError("Error creating assessment task subtask performance step.")

        return resp
    }

    public createAssessmentTaskPerformanceStep = async (
        assessmentTaskPerformanceStep: CreateAssessmentTaskPerformanceStepInput,
    ) => {
        const resp = await this.db.executeGQL("createAssessmentTaskPerformanceStep", {
            createAssessmentTaskPerformanceStepInput: assessmentTaskPerformanceStep
        }) as AssessmentTaskPerformanceStep | false

        if (!resp)
            return this.#handleError("Error creating assessment task performance step.")

        return resp
    }

    public defaultAssessmentTaskDataForIDs = (taskIDs: number[]): EMETLAssessmentTaskWithDetails[] => {
        return Object.keys(this.#staticTasksAndPS).reduce(
            (acc, taskNumber) => {
                const taskInfo = this.#staticTasksAndPS[taskNumber][0]
                if (taskInfo && taskIDs.includes(taskInfo.TaskID)) {
                    const d = this.assessmentTaskToDetailedTask({
                        ID: -taskInfo.TaskID,
                        TaskID: taskInfo.TaskID,
                        AssessmentID: null,
                        SpecificNeeds: "",
                        Rating: this.getDefaultTaskRating(),
                    })

                    d && acc.push(d)
                }
                return acc
            },
            [] as EMETLAssessmentTaskWithDetails[]
        )
    }

    public deleteAssessment = async (ID: number): Promise<Assessment | false> => {
        const resp = await this.db.executeGQL("deleteAssessment", {
            ID
        }) as Assessment | false

        if (!resp || resp.ID !== ID)
            return this.#handleError("Error deleting assessment.")

        return resp
    }

    public getDefaultTaskRating = () => {
        return EMETLAssessmentTaskRating.NOT_APPLICABLE
    }

    public getDefaultTrainingType = () => {
        return EMETLAssessmentTrainingType.TRAIN_OFF_POST
    }

    public getTaskRatingsWhichRequireNeeds = () => {
        return [
            EMETLAssessmentTaskRating.UNTRAINED,
            EMETLAssessmentTaskRating.NEEDS_PRACTICE,
        ]
    }

    public getTaskRatingsWithoutNeeds = () => {
        return this.TASK_RATINGS.filter(r => !this.getTaskRatingsWithoutNeeds().includes(r))
    }

    public updateAssessmentDetail = async (details: AssessmentDetail) => {
        if (details["__typename"])
            delete details["__typename"]
        
        const resp = await this.db.executeGQL("updateAssessmentDetail", {
            updateAssessmentDetailInput: details
        }) as AssessmentDetail | false
        if (!resp)
            return this.#handleError("Error updating assessment details.")

        return resp
    }

    public updateAssessment = async (
        assessment: EMETLDetailedAssessment, 
        username: string,
        assessmentTasks: string = null,
        deleteAssessmentTasks: string = null,
        assessed: string = null,
        deleteAssessed: string = null,
    ) => {
        const resp = await this.db.executeGQL("updateAssessment", {
            updateAssessmentInput: {
                Title: assessment.Title || "",
                ID: assessment.ID,
                BranchID: assessment.BranchID,
                Assessor: username,
                TrainingType: assessment.TrainingType,
                Created: assessment.Created,
                CreateAssessmentTasks: assessmentTasks,
                DeleteAssessmentTasks: deleteAssessmentTasks,
                CreateAssessed: assessed,
                DeleteAssessed: deleteAssessed,
            }
        }) as Assessment | false
        if (resp === false || resp?.ID !== assessment.ID) {
            return this.#handleError("Error updating assessment.")
        }

        return resp
    }

    public updateAssessmentTask = async (assessmentTask: AssessmentTask) => {
        const resp = await this.db.executeGQL("updateAssessmentTask", {
            updateAssessmentTaskInput: assessmentTask
        }) as AssessmentTask | false
        if (!resp || resp.ID !== assessmentTask.ID)
            return this.#handleError("Error updating assessment task.")

        return resp
    }

    public updateAssessmentTaskSubTask = async (assessmentTaskSubTask: AssessmentTaskSubTask) => {
        if (assessmentTaskSubTask["__typename"])
            delete assessmentTaskSubTask["__typename"]

        const resp = await this.db.executeGQL("updateAssessmentTaskSubTask", {
            updateAssessmentTaskSubTaskInput: assessmentTaskSubTask
        }) as AssessmentTaskSubTask | false
        if (!resp || resp.ID !== assessmentTaskSubTask.ID)
            return this.#handleError("Error updating assessment task subtask.")

        return resp
    }

    public updateAssessmentTaskSubTaskPerformanceStep = async (
        assessmentTaskSubTaskPerformanceStep: UpdateAssessmentTaskSubTaskPerformanceStepInput,
    ) => {
        if (assessmentTaskSubTaskPerformanceStep["__typename"])
            delete assessmentTaskSubTaskPerformanceStep["__typename"]
        
        const resp = await this.db.executeGQL("updateAssessmentTaskSubTaskPerformanceStep", {
            updateAssessmentTaskSubTaskPerformanceStepInput: assessmentTaskSubTaskPerformanceStep
        }) as AssessmentTaskSubTaskPerformanceStep | false

        if (!resp || resp.ID !== assessmentTaskSubTaskPerformanceStep.ID)
            return this.#handleError("Error updating assessment task subtask performance step.")

        return resp
    }

    public updateAssessmentTaskPerformanceStep = async (
        assessmentTaskPerformanceStep: AssessmentTaskPerformanceStep,
    ) => {
        if (assessmentTaskPerformanceStep["__typename"])
            delete assessmentTaskPerformanceStep["__typename"]
        
        const resp = await this.db.executeGQL("updateAssessmentTaskPerformanceStep", {
            updateAssessmentTaskPerformanceStepInput: assessmentTaskPerformanceStep
        }) as AssessmentTaskPerformanceStep | false
        if (!resp || resp.ID !== assessmentTaskPerformanceStep.ID)
            return this.#handleError("Error updating assessment task performance step.")

        return resp
    }

    public anyDetailsAreSet = (assessment: EMETLDetailedAssessment): boolean => {
        return Boolean(
            !!assessment.Conditions || !!assessment.Results || !!assessment.Reference || !!assessment.Location || !!assessment.Scenario || assessment.IsTemplate
        )
    }

    /**
     * Loops through pst object & adds an array of performance steps for each subtask within pst.
     * 
     * @param {object} pst - Object containing a list of tasks.
     */
    public addPSData = (pst: EMETLStaticTaskStructure) => {
        Object.keys(pst).forEach(
            taskNumber => {
                for (const i in pst[taskNumber]) {
                    const psOrTaskObject = pst[taskNumber][i] as SubtaskFlat
                    if(psOrTaskObject.SubTaskNumber)
                        psOrTaskObject.PerformanceSteps = pst[psOrTaskObject.SubTaskNumber] as PerformanceStepFlat[]
                }
            }
        )
    }

    /**
     * Loops through psForTasks & creates a task object for each tasks. Subtasks are appended to collective tasks.
     * 
     */
    public buildTaskStructure = (psForTasks: EMETLTaskStructureAPIResponse): EMETLStaticTaskStructure => {
        this.#staticTasksAndPS = {}
        psForTasks[0].forEach((ps: EMETLTaskStructureAPIResponseEntry) => {
            if (ps.Number.includes("-")) {
                this.#staticTasksAndPS[ps.Number] = this.#staticTasksAndPS[ps.Number] ?? []

                if (ps.PSID) {
                    this.#staticTasksAndPS[ps.Number].push({
                        "PerformanceStepName": ps.StepName,
                        "PerformanceStepSequence": ps.StepSequence,
                        "TaskID": ps.ID,
                        "TaskNumber": ps.Number,
                        "TaskName": ps.Name,
                        "PSID": ps.PSID,

                    })
                } else if (ps.SubTaskID) {
                    this.#staticTasksAndPS[ps.Number].push({
                        "SubTaskID": ps.SubTaskID,
                        "SubTaskNumber": ps.SubTaskNumber,
                        "SubTaskName": ps.SubTaskName,
                        "PerformanceSteps": [],
                        "TaskID": ps.ID,
                        "TaskNumber": ps.Number,
                        "TaskName": ps.Name,
                    })
                }
            }
        })
        this.addPSData(this.#staticTasksAndPS)

        return this.#staticTasksAndPS
    }

    public loadStaticTasksAndPS = async () => {
        const params = {
            "getPSForTasks": true,
        }
        const staticDataResp = await this.getNestedAssessmentData(params)
        if (staticDataResp && !!Object.keys(staticDataResp)) {
            try {
                const staticData = JSON.parse(staticDataResp)?.body

                return this.buildTaskStructure(staticData)
            } catch (e) {
                return this.#handleError("Error fetching static assessment data.")
            }
        }
        
        return this.#handleError("Error fetching static assessment data.")
    }

    public subscribe = async (handler: (resp: object) => void) => {
        const onCreateAssessments = await this.db.executeGQL("onCreateAssessment") as Observable<object>
        const resp = onCreateAssessments.subscribe({
            next: resp => {
                handler(resp)
            }
        })
        this.#subscriptions.push(resp)
    }

    public unsubscribe = () => {
        this.#subscriptions.forEach(
            sub => {
                sub && sub?.unsubscribe()
            }
        )
    }

    public loadTasks = async () => {
        const resp = await this.db.executeGQL("listTasks") as Task[]
        if (!resp)
            return this.#handleError("Error fetching tasks.")

        return resp
    }

    public loadAssessmentTasks = async (assessmentID: number): Promise<EMETLAssessmentTaskWithDetails[] | false> => {
        const resp = await this.db.executeGQL("listAssessmentTasks", {
            AssessmentID: assessmentID,
        }) as AssessmentTask[]
        if (!resp)
            return this.#handleError("Error fetching assessment tasks.")

        return resp.map(assessmentTask => this.assessmentTaskToDetailedTask(assessmentTask)).filter(t => t !== null)
    }

    public loadSubtasksForAssessmentTask = async (
        assessmentTaskID: number,
    ): Promise<AssessmentTaskSubTask[] | false> => {
        const resp = await this.db.executeGQL("listAssessmentTaskSubTasks", {
            ATID: assessmentTaskID,
        }) as AssessmentTaskSubTask[]

        if (!resp)
            return this.#handleError("Error fetching assessment task subtasks.")

        return resp
    }

    public loadPerformanceStepsForAssessmentTask = async (assessmentTaskID: number) => {
        const resp = await this.db.executeGQL("listAssessmentTaskPerformanceSteps", {
            ATID: assessmentTaskID,
        }) as AssessmentTaskPerformanceStep[]
        if (!resp)
            return this.#handleError("Error fetching assessment task performance steps.")

        return resp
    }

    public loadPerformanceStepsForAssessmentTaskSubTask = async (
        assessmentTaskSubTaskID: number,
    ): Promise<AssessmentTaskSubTaskPerformanceStep[] | false> => {
        const resp = await this.db.executeGQL("listAssessmentTaskSubTaskPerformanceSteps", {
            ATSTID: assessmentTaskSubTaskID,
        }) as AssessmentTaskSubTaskPerformanceStep[]
        if (!resp)
            return this.#handleError("Error fetching assessment task performance steps.")

        return resp
    }

    public performanceStepToDetailedPerformanceStep = (
        performanceStep: AssessmentTaskPerformanceStep,
        assessmentTask: EMETLAssessmentTaskWithDetails,
    ): EMETLAssessmentTaskPerformanceStepWithDetails => {
        const taskNumber = assessmentTask.Number
        const performanceStepDetails = this.ctx.staticTasksAndPS.value[taskNumber]?.find(
            (ps: PerformanceStepFlat) => ps.PSID === performanceStep.PSID
        ) as PerformanceStepFlat
        if (!performanceStepDetails) {
            this.#handleError(`Performance step with ID ${performanceStep.PSID} not found for task ${taskNumber}.`)
            return null
        }

        return {
            ...performanceStepDetails,
            ...performanceStep,
        }
    }

    public assessmentTaskToDetailedTask = (assessmentTask: CreateAssessmentTaskInput): EMETLAssessmentTaskWithDetails => {
        let resp = null
        Object.keys(this.ctx.staticTasksAndPS.value)?.forEach(
            taskNumber => {
                const task = this.ctx.staticTasksAndPS.value[taskNumber][0]
                if (!task)
                    return
                if (assessmentTask.TaskID === task.TaskID) {                    
                    resp = {
                        ...assessmentTask,
                        Number: task.TaskNumber,
                        Name: task.TaskName,
                    }
                }
            }
        )

        return resp
        // const staticTaskInfo = staticTasksAndPS[task.Number]
    }

    public detailedAssessmentToAssessment = (detailedAssessment: EMETLDetailedAssessment): Assessment => {
        return {
            ID: detailedAssessment.ID,
            Title: detailedAssessment.Title,
            BranchID: detailedAssessment.BranchID,
            Assessor: detailedAssessment.Assessor,
            TrainingType: detailedAssessment.TrainingType,
            Created: detailedAssessment.Created,
            __typename: "Assessment",
        }
    }

    public detailedAssessmentToAssessmentDetail = (detailedAssessment: EMETLDetailedAssessment): AssessmentDetail => {
        return {
            ID: detailedAssessment.AssessmentDetailID,
            AssessmentID: detailedAssessment.ID,
            Conditions: detailedAssessment.Conditions,
            Results: detailedAssessment.Results,
            Reference: detailedAssessment.Reference,
            IsTemplate: parseInt(`${detailedAssessment.IsTemplate ?? "0"}`),
            Location: detailedAssessment.Location,
            Scenario: detailedAssessment.Scenario,
            __typename: "AssessmentDetail",
        }
    }

    public detailedAssessmentToQuarterlyAssessmentDetail = (detailedAssessment: EMETLDetailedAssessment): QuarterlyAssessmentDetail => {
        return {
            ID: detailedAssessment.QuarterlyAssessmentDetailID,
            AssessmentID: detailedAssessment.ID,
            StartDate: detailedAssessment.StartDate,
            EndDate: detailedAssessment.EndDate,
            __typename: "QuarterlyAssessmentDetail",
        }
    }

    public getAssessmentTasks = async (
        assessmentID: number,
    ) => {

        const resp = await this.loadAssessmentTasks(assessmentID) || []
        if (resp?.length) {
            return resp.sort((a, b) => {
                if (a.Number < b.Number) return -1
                if (a.Number > b.Number) return 1
                return 0
            })
        }

        return resp
    }

    public listAssessedUsersForAssessment = async (assessmentID: number): Promise<Assessed[]> => {
        const resp = await this.db.executeGQL("listAssesseds", {
            AssessmentID: assessmentID,
        }) as Assessed[]
        
        return resp
    }

    public taskHasSubtasks = (task: EMETLAssessmentTaskWithDetails) => {
        const typeOfTask = this.taskType(task)
        
        return typeOfTask === EMETLTaskType.COLLECTIVE
    }

    public taskHasPerformanceSteps = (task: EMETLAssessmentTaskWithDetails) => {
        return !this.taskHasSubtasks(task)
    }

    public taskType = (task: EMETLAssessmentTaskWithDetails): EMETLTaskType => {
        if (task.Number.includes("COLLECT")) {
            return EMETLTaskType.COLLECTIVE
        } else if (task.Number.includes("LEAD")) {
            return EMETLTaskType.LEADER
        } else if (task.Number.includes("SITE")) {
            return EMETLTaskType.SITE_SPECIFIC
        } else if (task.Number.includes("IND")) {
            return EMETLTaskType.INDIVIDUAL
        }
    }

    public getNestedAssessmentData = async (params: object) => {
        const resp = await this.db.executeLambda(
            "GetNestedAssessmentData.getNestedAssessmentData", 
            params,
        )
        if (!resp)
            return this.#handleError("Error fetching assessment data.")

        const { 
            result,
        } = resp

        return result
    }

    public loadAssessmentData = async (): Promise<Assessment[]> => {
        this.#assessments = await this.db.executeGQL("listAssessments") as Assessment[]

        return this.assessments
    }

    public loadQuarterlyAssessmentData = async (): Promise<Assessment[]> => {
        const resp = await this.db.executeGQL("listAssessments", {
            WantsQuarterlyOnly: true,
        }) as Assessment[]

        return resp
    }

    public loadTemplates = async (): Promise<ListAssessmentResult[]> => {
        const resp = await this.db.executeGQL("listAssessments", {
            WantsTemplatesOnly: true,
        }) as ListAssessmentResult[]

        return resp
    }

    public loadAssessment = async (assessmentID: number): Promise<EMETLDetailedAssessment | false> => {
        const assessment = await this.db.executeGQL("getAssessment", {
            ID: assessmentID,
        }) as EMETLDetailedAssessment

        if (!assessment)
            return this.#handleError("Error fetching assessment.")

        return assessment
    }

    public loadUser = async (userID: string): Promise<User | false> => {
        const user = await this.db.executeGQL("getUser", {
            ID: userID,
        }) as User

        if (!user)
            return this.#handleError("Error fetching user.")

        return user
    }

    public getUnitForBranch = async (branchID: number): Promise<Unit | false> => {
        const unit = await this.db.executeGQL("getUnit", {
            BranchID: branchID,
        }) as Unit

        if (!unit)
            return this.#handleError("Error fetching unit.")

        return unit
    }

    public getBranchForUnit = async (unitID: number, unitDepth: number): Promise<Branch | false> => {
        const branch = await this.db.executeGQL("getBranch", {
            UnitID: unitID,
            UnitDepth: unitDepth,
        }) as Branch

        if (!branch)
            return this.#handleError("Error fetching branch.")

        return branch
    }

    public listUnitsForUser = async (): Promise<Unit[]> => {
        this.#units = await this.db.executeGQL("listUnits") as Unit[]

        return this.#units
    }

    public get assessments(): Assessment[] {
        return this.#assessments
    }

    public set handleError(handler: (message: string) => false) {
        this.#handleError = handler
    }

    public get handleError() {
        return this.#handleError
    }

    public get TASK_RATINGS(): string[] {
        return this.#TASK_RATINGS
    }

    public get PS_RATINGS(): string[] {
        return this.#PS_RATINGS
    }

    public get TRAINING_TYPES(): string[] {
        return this.#TRAINING_TYPES
    }
}

const EMETLAssessmentManager = new EMETLAssessmentTools()

export default EMETLAssessmentManager